/**
 * Email validation utility for frontend
 * Validates email format before saving to sessionStorage or sending to backend
 */

/**
 * Validates if an email address has a valid format
 * @param email - Email address to validate
 * @returns true if email format is valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== "string") {
    return false;
  }

  const trimmedEmail = email.trim();

  if (trimmedEmail.length === 0 || trimmedEmail.length > 254) {
    return false;
  }

  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(trimmedEmail)) {
    return false;
  }
  const dangerousPatterns = [
    /[<>\"'%;()&+]/g,
    /\s/g,
    /\.\./g,
    /@.*@/g,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(trimmedEmail)) {
      return false;
    }
  }

  const parts = trimmedEmail.split("@");
  if (parts.length !== 2) {
    return false;
  }

  const domainPart = parts[1];
  if (!domainPart.includes(".")) {
    return false;
  }

  const domainParts = domainPart.split(".");
  const tld = domainParts[domainParts.length - 1];
  if (!tld || tld.length < 2) {
    return false;
  }

  return true;
}

/**
 * Sanitizes an email address by trimming and converting to lowercase
 * @param email - Email address to sanitize
 * @returns Sanitized email address or null if invalid
 */
export function sanitizeEmail(email: string): string | null {
  if (!email || typeof email !== "string") {
    return null;
  }

  const trimmed = email.trim().toLowerCase();

  const sanitized = trimmed.substring(0, 254);

  if (!isValidEmail(sanitized)) {
    return null;
  }

  return sanitized;
}

/**
 * Validates and sanitizes an email address
 * @param email - Email address to validate and sanitize
 * @returns Object with validation result and sanitized email
 */
export function validateAndSanitizeEmail(email: string): {
  isValid: boolean;
  sanitized: string | null;
  error?: string;
} {
  if (!email || typeof email !== "string") {
    return {
      isValid: false,
      sanitized: null,
      error: "Email is required",
    };
  }

  const trimmed = email.trim();

  if (trimmed.length === 0) {
    return {
      isValid: false,
      sanitized: null,
      error: "Email cannot be empty",
    };
  }

  if (trimmed.length > 254) {
    return {
      isValid: false,
      sanitized: null,
      error: "Email address is too long (maximum 254 characters)",
    };
  }

  const sanitized = sanitizeEmail(email);

  if (!sanitized) {
    return {
      isValid: false,
      sanitized: null,
      error: "Invalid email format",
    };
  }

  return {
    isValid: true,
    sanitized: sanitized,
  };
}

const ACCEPTED_EMAIL_DOMAIN_REGEX =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

export function isValidAcceptedEmailDomain(domain: string): boolean {
  if (!domain || typeof domain !== "string") {
    return false;
  }

  const trimmed = domain.trim();
  if (!trimmed || trimmed.includes("@") || /\s/.test(trimmed)) {
    return false;
  }

  if (/[^a-zA-Z0-9.-]/.test(trimmed)) {
    return false;
  }

  if (trimmed.startsWith(".") || trimmed.endsWith(".") || trimmed.includes("..")) {
    return false;
  }

  return ACCEPTED_EMAIL_DOMAIN_REGEX.test(trimmed);
}

export function getAcceptedEmailDomainValidationError(
  domain: string,
): string | null {
  const trimmed = domain.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes("@")) {
    return "Enter only the domain (e.g. acme.com), without @.";
  }

  if (/\s/.test(trimmed)) {
    return "Email domain cannot contain spaces.";
  }

  if (/[^a-zA-Z0-9.-]/.test(trimmed)) {
    return "Email domain contains invalid characters.";
  }

  if (!isValidAcceptedEmailDomain(trimmed)) {
    return "Enter a valid email domain (e.g. acme.com).";
  }

  return null;
}

